/**
 * Service for project-related business logic
 */
export class ProjectService {
    /**
     * Calculate project progress based on completed tasks
     */
    static calculateProgress(taskCount, completedTaskCount) {
        if (taskCount === 0)
            return 0;
        return Math.round((completedTaskCount / taskCount) * 100);
    }
    /**
     * Calculate total investment from completed tasks
     */
    static calculateTotalInvestment(tasks) {
        return tasks
            .filter((task) => task.status === "complete" && task.amount)
            .reduce((total, task) => total + (task.amount || 0), 0);
    }
    /**
     * Get project status color for UI
     */
    static getStatusColor(status) {
        const colors = {
            to_do: "gray",
            in_progress: "blue",
            complete: "green",
            archived: "gray",
        };
        return colors[status] || "gray";
    }
    /**
     * Get project status label
     */
    static getStatusLabel(status) {
        const labels = {
            to_do: "To Do",
            in_progress: "In Progress",
            complete: "Complete",
            archived: "Archived",
        };
        return labels[status] || status;
    }
    /**
     * Check if project is overdue
     */
    static isOverdue(project) {
        if (!project.targetEndDate || project.status === "complete" || project.status === "archived") {
            return false;
        }
        return new Date(project.targetEndDate) < new Date();
    }
    /**
     * Get project duration in days
     */
    static getProjectDuration(startDate, endDate) {
        if (!startDate || !endDate)
            return null;
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = Math.abs(end.getTime() - start.getTime());
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }
    /**
     * Calculate cost efficiency (actual vs estimated)
     */
    static calculateCostEfficiency(estimatedCost, totalInvestment) {
        if (!estimatedCost || estimatedCost === 0)
            return null;
        if (!totalInvestment)
            return 100; // No investment yet, 100% efficient
        return Math.round(((estimatedCost - totalInvestment) / estimatedCost) * 100);
    }
    /**
     * Get project health status based on progress, timeline, and cost
     */
    static getProjectHealth(project) {
        const isOverdue = this.isOverdue(project);
        const costEfficiency = this.calculateCostEfficiency(project.estimatedCost || undefined, project.totalInvestment || undefined);
        const progress = project.progress || 0;
        // Critical conditions
        if (isOverdue && progress < 80)
            return "critical";
        if (costEfficiency !== null && costEfficiency < -50)
            return "critical";
        // Warning conditions
        if (isOverdue || (costEfficiency !== null && costEfficiency < -20))
            return "warning";
        if (progress < 30 && project.status === "in_progress")
            return "warning";
        return "healthy";
    }
    /**
     * Filter projects based on user role and permissions
     */
    static filterProjectsByRole(projects, userId, role, isOwner) {
        // Owners and admins see all projects
        if (isOwner || role === "admin" || role === "project_manager") {
            return projects;
        }
        // Clients and contractors only see projects they're assigned to
        return projects.filter((project) => project.assignments.some((assignment) => assignment.userId === userId) || project.createdBy === userId);
    }
    /**
     * Sort projects by priority (overdue, in progress, etc.)
     */
    static sortProjectsByPriority(projects) {
        return [...projects].sort((a, b) => {
            // First, sort by health status (critical first)
            const healthA = this.getProjectHealth(a);
            const healthB = this.getProjectHealth(b);
            const healthOrder = { critical: 0, warning: 1, healthy: 2 };
            if (healthA !== healthB) {
                return healthOrder[healthA] - healthOrder[healthB];
            }
            // Then by status priority
            const statusOrder = {
                in_progress: 0,
                to_do: 1,
                complete: 2,
                archived: 3,
            };
            const statusA = statusOrder[a.status] || 4;
            const statusB = statusOrder[b.status] || 4;
            if (statusA !== statusB) {
                return statusA - statusB;
            }
            // Finally by updated date (most recent first)
            return new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime();
        });
    }
    /**
     * Validate project data before submission
     */
    static validateProjectData(data) {
        const errors = [];
        if ("name" in data && data.name && data.name.trim().length === 0) {
            errors.push("Project name is required");
        }
        if ("name" in data && data.name && data.name.length > 100) {
            errors.push("Project name must be less than 100 characters");
        }
        if (data.description && data.description.length > 1000) {
            errors.push("Description must be less than 1000 characters");
        }
        if (data.estimatedCost && data.estimatedCost < 0) {
            errors.push("Estimated cost cannot be negative");
        }
        if (data.startDate && data.targetEndDate) {
            const start = new Date(data.startDate);
            const end = new Date(data.targetEndDate);
            if (start >= end) {
                errors.push("Target end date must be after start date");
            }
        }
        return errors;
    }
    /**
     * Format currency for display
     */
    static formatCurrency(amount, currency = "USD") {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    }
    /**
     * Format date for display
     */
    static formatDate(dateString) {
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    }
    /**
     * Get relative time (e.g., "2 days ago", "in 3 days")
     */
    static getRelativeTime(dateString) {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = date.getTime() - now.getTime();
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
        if (diffDays === 0)
            return "Today";
        if (diffDays === 1)
            return "Tomorrow";
        if (diffDays === -1)
            return "Yesterday";
        if (diffDays > 0)
            return `In ${diffDays} days`;
        return `${Math.abs(diffDays)} days ago`;
    }
}
